import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton
} from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Refresh,
  CalendarMonth
} from '@mui/icons-material';
import { packagesService, departureDateService } from '../services';
import { useToast } from '../contexts/ToastContext';
import DepartureDateFormDialog from './dialog/departure/DepartureDateFormDialog';
import DepartureDateDetailDialog from './dialog/departure/DepartureDateDetailDialog';

const DepartureDateManagement = () => {
  // State management
  const [departureDates, setDepartureDates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Filter states
  const [filters, setFilters] = useState({
    package_id: '',
    is_active: '',
    search: ''
  });

  // Dialog states
  const [selectedDepartureDate, setSelectedDepartureDate] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { showToast } = useToast();

  // Load departure dates data
  const loadDepartureDates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        ...filters
      };

      const response = await departureDateService.getDepartureDates(params);
      setDepartureDates(response.data.departureDates || []);
      setTotalRows(response.total || 0);
    } catch (error) {
      console.error('Error loading departure dates:', error);
      showToast('Error loading departure dates: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filters, showToast]);

  // Load packages for filter dropdown
  const loadPackages = useCallback(async () => {
    try {
      const packagesData = await packagesService.getPackages({ limit: 20 }); // Get all packages for dropdown
      setPackages(packagesData.packages || []);
    } catch (error) {
      console.error('Error loading packages:', error);
    }
  }, []);

  // Load data on component mount and when filters change
  useEffect(() => {
    loadDepartureDates();
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      package_id: '',
      is_active: '',
      search: ''
    });
  };

  // Handle departure date actions
  const handleViewDepartureDate = (departureDate) => {
    setSelectedDepartureDate(departureDate);
    setViewDialogOpen(true);
  };

  const handleEditDepartureDate = (departureDate) => {
    setSelectedDepartureDate(departureDate);
    setIsEditing(true);
    setFormDialogOpen(true);
  };

  const handleCreateDepartureDate = () => {
    setSelectedDepartureDate(null);
    setIsEditing(false);
    setFormDialogOpen(true);
  };

  const handleDeleteDepartureDate = (departureDate) => {
    setSelectedDepartureDate(departureDate);
    setDeleteDialogOpen(true);
  };

  const handleToggleStatus = async (departureDate) => {
    try {
      await departureDateService.toggleStatus(departureDate.id);
      showToast('Departure date status updated successfully', 'success');
      loadDepartureDates();
    } catch (error) {
      console.error('Error toggling departure date status:', error);
      showToast('Error updating departure date status: ' + error.message, 'error');
    }
  };

  const confirmDeleteDepartureDate = async () => {
    try {
      await departureDateService.deleteDepartureDate(selectedDepartureDate.id);
      setDeleteDialogOpen(false);
      setSelectedDepartureDate(null);
      showToast('Departure date deleted successfully', 'success');
      loadDepartureDates();
    } catch (error) {
      setErrorMessage(error.response.data.error.message)
      console.error('Error deleting departure date:', error);
      showToast('Error deleting departure date: ' + error.message, 'error');
    }
  };

  // Custom toolbar component
  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Button
        size="small"
        startIcon={<Add />}
        onClick={handleCreateDepartureDate}
        sx={{ ml: 1 }}
        color="primary"
        variant="contained"
      >
        Add Departure Date
      </Button>
      <Button
        size="small"
        startIcon={<Refresh />}
        onClick={loadDepartureDates}
        sx={{ ml: 1 }}
      >
        Refresh
      </Button>
    </GridToolbarContainer>
  );

  // Define data grid columns
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      type: 'number'
    },
    {
      field: 'package_name',
      headerName: 'Package',
      width: 250,
      valueGetter: (params) => params.row.package_name || 'N/A',
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {params.row.package_name || 'N/A'}
          </Typography>
          <Typography variant="caption" color="textSecondary" noWrap>
            {params.row.category_name || 'No category'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'departure_date',
      headerName: 'Departure Date',
      width: 150,
      type: 'date',
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'return_date',
      headerName: 'Return Date',
      width: 150,
      type: 'date',
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'days_duration',
      headerName: 'Duration',
      width: 100,
      type: 'number',
      valueGetter: (params) => {
        const departure = new Date(params.row.departure_date);
        const returnDate = new Date(params.row.return_date);
        const diffTime = Math.abs(returnDate - departure);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      },
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value} days
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <FormControlLabel
          control={
            <Switch
              checked={params.value}
              onChange={() => handleToggleStatus(params.row)}
              size="small"
            />
          }
          label=""
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      type: 'date',
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewDepartureDate(params.row)}
              sx={{ color: 'primary.main' }}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditDepartureDate(params.row)}
              sx={{ color: 'warning.main' }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteDepartureDate(params.row)}
              sx={{ color: 'error.main' }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Departure Dates Management
      </Typography>

      {/* Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search packages..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Package</InputLabel>
                <Select
                  value={filters.package_id}
                  onChange={(e) => handleFilterChange('package_id', e.target.value)}
                  label="Package"
                >
                  <MenuItem value="">All Packages</MenuItem>
                  {packages.map(pkg => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      {pkg.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={departureDates}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={totalRows}
              loading={loading}
              paginationMode="server"
              pageSizeOptions={[10, 25, 50, 100]}
              slots={{ toolbar: CustomToolbar }}
              checkboxSelection
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Departure Date Form Dialog */}
      <DepartureDateFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        departureDate={selectedDepartureDate}
        isEditing={isEditing}
        packages={packages}
        onSave={() => {
          setFormDialogOpen(false);
          loadDepartureDates();
        }}
      />

      {/* View Departure Date Dialog */}
      {selectedDepartureDate && (
        <DepartureDateDetailDialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          departureDate={selectedDepartureDate}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this departure date?
            This action cannot be undone and may affect existing bookings.
          </Typography>

          {selectedDepartureDate && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                Package: {selectedDepartureDate.package?.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Departure:{" "}
                {new Date(
                  selectedDepartureDate.departure_date
                ).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Return:{" "}
                {new Date(
                  selectedDepartureDate.return_date
                ).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteDepartureDate} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DepartureDateManagement;